// Computes "total mg per vial" for injectable products from the
// human-readable `concentration` and `size` strings on a Product.
//
// Convention (per Bloom): when a concentration is written as a bare "X mg"
// without "/mL", we treat it as mg/mL anyway — this matches how pharmacy
// data is entered today. We surface a `perMlMissing` flag so admins can
// clean up entries that drop the "/mL" suffix.
//
// Non-vial formats (ODT, tablet, capsule, cream, etc.) return isVial:false
// and no total — there's no meaningful "mg per vial" for them.

export interface VialMath {
  isVial: boolean;
  totalMg: number | null;
  mgPerMl: number | null;
  volumeMl: number | null;
  perMlMissing: boolean; // true when concentration string lacked "/mL"
}

const NUM = /(\d+(?:\.\d+)?)/;
const MG_PER_ML_RE = new RegExp(`${NUM.source}\\s*mg\\s*(?:/|per\\s*)\\s*ml`, 'i');
const BARE_MG_RE = new RegExp(`${NUM.source}\\s*mg\\b`, 'i');
const ML_RE = new RegExp(`${NUM.source}\\s*ml\\b`, 'i');

const isInjectable = (format?: string): boolean =>
  !!format && /injectable/i.test(format);

export function computeVialMath(product: {
  format?: string;
  concentration?: string;
  size?: string;
}): VialMath {
  const empty: VialMath = {
    isVial: false,
    totalMg: null,
    mgPerMl: null,
    volumeMl: null,
    perMlMissing: false,
  };

  if (!isInjectable(product.format)) return empty;

  const conc = product.concentration || '';
  const size = product.size || '';

  const explicitMatch = conc.match(MG_PER_ML_RE);
  const bareMatch = !explicitMatch ? conc.match(BARE_MG_RE) : null;
  const mlMatch = size.match(ML_RE);

  const mgPerMl = explicitMatch
    ? parseFloat(explicitMatch[1])
    : bareMatch
      ? parseFloat(bareMatch[1])
      : null;
  const volumeMl = mlMatch ? parseFloat(mlMatch[1]) : null;

  if (mgPerMl === null || volumeMl === null || !isFinite(mgPerMl) || !isFinite(volumeMl)) {
    return { ...empty, isVial: true, perMlMissing: !explicitMatch && !!bareMatch };
  }

  return {
    isVial: true,
    totalMg: mgPerMl * volumeMl,
    mgPerMl,
    volumeMl,
    perMlMissing: !explicitMatch,
  };
}

const fmtNum = (n: number): string => {
  // Show up to 2 decimals, trim trailing zeros.
  return Number.isInteger(n) ? n.toString() : parseFloat(n.toFixed(2)).toString();
};

// "Total: 50 mg" — short label for cards/rows.
export function formatTotalMgPerVial(v: VialMath): string | null {
  if (!v.isVial || v.totalMg === null) return null;
  return `Total: ${fmtNum(v.totalMg)} mg`;
}

// "50 mg" — bare value, for use under a column header that already says "Total mg".
export function formatTotalMgBare(v: VialMath): string | null {
  if (!v.isVial || v.totalMg === null) return null;
  return `${fmtNum(v.totalMg)} mg`;
}

// "10 mg/mL × 5 mL = 50 mg" — verbose form for detail views.
export function formatVialBreakdown(v: VialMath): string | null {
  if (!v.isVial || v.totalMg === null || v.mgPerMl === null || v.volumeMl === null) return null;
  return `${fmtNum(v.mgPerMl)} mg/mL × ${fmtNum(v.volumeMl)} mL = ${fmtNum(v.totalMg)} mg`;
}
